import { useEffect, useRef } from "react"
import type { Puzzle } from "@shared/types.ts"
import { ClueItem } from "./ClueItem.tsx"
import type { CellClueInfo } from "../lib/grid-utils.ts"

interface CluePanelProps {
  puzzle: Puzzle
  currentCell: [number, number] | null
  direction: "across" | "down"
  cellClueMap: Map<string, CellClueInfo>
  onClueClick: (number: number, direction: "across" | "down") => void
}

export function CluePanel({
  puzzle,
  currentCell,
  direction,
  cellClueMap,
  onClueClick,
}: CluePanelProps) {
  const activeClueRef = useRef<HTMLDivElement>(null)

  // Get the active clue number
  let activeAcross: number | undefined
  let activeDown: number | undefined
  if (currentCell) {
    const info = cellClueMap.get(`${currentCell[0]},${currentCell[1]}`)
    if (info) {
      activeAcross = info.across?.number
      activeDown = info.down?.number
    }
  }

  const primaryActiveNumber = direction === "across" ? activeAcross : activeDown

  const panelRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to active clue within the clue panel only.
  // Uses panel.scrollTop so the *page* never scrolls (which would hide the grid on mobile).
  useEffect(() => {
    const el = activeClueRef.current
    const panel = panelRef.current
    if (!el || !panel) return

    // Only scroll if the panel is actually a scroll container (has bounded height).
    // On mobile the panel has max-height: none and isn't scrollable — skip entirely.
    if (panel.scrollHeight <= panel.clientHeight) return

    const elTop = el.offsetTop
    const elBottom = elTop + el.offsetHeight
    const panelScrollTop = panel.scrollTop
    const panelBottom = panelScrollTop + panel.clientHeight

    if (elTop < panelScrollTop) {
      panel.scrollTo({ top: elTop, behavior: "smooth" })
    } else if (elBottom > panelBottom) {
      panel.scrollTo({ top: elBottom - panel.clientHeight, behavior: "smooth" })
    }
  }, [primaryActiveNumber, direction])

  return (
    <div className="clue-panel" ref={panelRef}>
      <div className="clue-section">
        <h3>Across</h3>
        {puzzle.clues.across.map((clue) => {
          const isActive = direction === "across" && clue.number === activeAcross
          return (
            <div key={`a-${clue.number}`} ref={isActive ? activeClueRef : undefined}>
              <ClueItem
                clue={clue}
                isActive={isActive}
                onClick={() => onClueClick(clue.number, "across")}
              />
            </div>
          )
        })}
      </div>
      <div className="clue-section">
        <h3>Down</h3>
        {puzzle.clues.down.map((clue) => {
          const isActive = direction === "down" && clue.number === activeDown
          return (
            <div key={`d-${clue.number}`} ref={isActive ? activeClueRef : undefined}>
              <ClueItem
                clue={clue}
                isActive={isActive}
                onClick={() => onClueClick(clue.number, "down")}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
